<template>
    <UApp :toaster="{ position: 'bottom-right' }" :locale="uiLocale">
        <UTheme :props="themeProps">
        <div class="app-container min-h-dvh">
            <!-- Top Progress Bar (old admin: v-progress-linear indeterminate, z-index 999999) -->
            <div v-if="loading" class="fixed inset-x-0 top-0 z-[999999]">
                <UProgress :model-value="null" size="xs" color="primary" />
            </div>

            <!-- Toast host: UApp toaster; every toast is added through @/composables/useToast, which sets its duration and layout -->

            <!-- Confirm host: the one shared CodConfirm — useConfirm() resolves through it -->
            <CodConfirm v-model="confirmOpen" :config="confirmConfig" />

            <!-- Handle Subscription / Contract Expired -->
            <!-- parity: pre-existing venue-locale gating (docs/AdminFeature.md, ex-parity-spec §6).
                 An inactive account (expired / disabled) gets Contract's account lock in every market. -->
            <Contract v-if="tenant && tenant.venue && (accountInactive || ['me', 'sr', 'ba'].includes(tenant.venue.locale))" />
            <Subscription v-if="tenant && tenant.venue && !accountInactive && !['me', 'sr', 'ba'].includes(tenant.venue.locale)" />

            <!-- Notification -->
            <Notification />

            <!-- App Content (RouterTransition died with the parity bridge — AdminFeature.md § History) -->
            <RouterView />
        </div>
        </UTheme>
    </UApp>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, watch } from 'vue'
import { AppEvent, TenantService } from 'app-core'
import { storeToRefs } from 'pinia'
import { en as uiEn } from '@nuxt/ui/locale'
import { useI18n } from 'vue-i18n'
import { CodConfirm, uiSr } from 'app-components'
import { useAppStore } from '@/stores/useAppStore'
import { useLoading } from '@/composables/useLoading'
import { useAppEvents } from '@/composables/useAppEvents'
import { useConfirmHost, showConfirm } from '@/composables/useConfirm'
import { registerSheet, unregisterSheet } from '@/utilities/sheetRegistry'
import { useToast } from '@/composables/useToast'
import Notification from '@/components/Notification.vue'
import Subscription from '@/components/Subscription.vue'
import Contract from '@/components/Contract.vue'

const { loading } = useLoading()
const { isOnline, tenant, isDark } = storeToRefs(useAppStore())
const accountInactive = computed(() => TenantService.isInactive(tenant.value))

// Dark mode = the `.dark` class on <html> (AD-10, POS mechanism; colorMode: false
// in vite.config.ts keeps Nuxt UI out of it). store.isDark = the user's choice
// (store.darkTheme, persisted via Store.set → appStorage, restored by
// AppService.initializeStore) or, until they make one, light.
// The login screens are self-fenced dark islands (local `dark` class), so no
// per-route forcing is needed here.
watch(isDark, dark => {
    document.documentElement.classList.toggle('dark', !!dark)
}, { immediate: true })
const appEvents = useAppEvents()
const { confirmOpen, confirmConfig } = useConfirmHost()
const toast = useToast()

// Kit seams (redesign P4): the shared forms toast through the app's own
// snackbar policy and open confirm dialogs on the singleton CodConfirm host.
provide('kontyToast', (message: string, status: 'success' | 'error') =>
    toast.add({ description: message, color: status === 'error' ? 'error' : 'success' }))
provide('codConfirm', showConfirm)
// AR-24: kit form footers render content-sized right-aligned on desktop here
// (they stretch full-width in POS and on any mobile viewport).
provide('kontyBackoffice', true)
// AR-26: CodSheets join the back-close registry (router/guards.ts consumes it).
provide('kontySheetRegistry', { register: registerSheet, unregister: unregisterSheet })

// Nuxt UI internal strings (table/select/search empty states, pagination, calendar) —
// old admin localized these via Vuetify's sr-Latn lang pack for sr/me/ba. The kit's
// `uiSr` is Serbian wording over Nuxt UI's bundled `hr` pack (its `hr` code — and so
// reka's number/date behaviour — unchanged); `en` for English. The pack follows the UI
// language (vue-i18n's locale, set by Locale.setLanguage), not the market: an
// English-language venue on a Serbian account reads English throughout.
const { locale: uiLanguage } = useI18n()
const uiLocale = computed(() => (uiLanguage.value === 'en' ? uiEn : uiSr))

// Number fields need nothing here: the kit NumberField owns its decimals, dot
// decimal point (a typed comma included), no grouping, no snapping, no wheel.
// Pickers (UInputMenu) open on a click anywhere in the field, not only on the
// chevron — reka's combobox otherwise waits for typing.
const themeProps = {
    inputMenu: {
        openOnClick: true
    }
}

// The old `darkbg` login override died at P6.2 — both auth screens are
// self-fenced dark islands that paint their own full-height canvas.

onMounted(async () => {
    // Port of old App.vue created(): hide the index.html splash …
    const appLoading = document.getElementById('app-loading')
    if (appLoading) appLoading.style.display = 'none'

    // … request persistent storage for the site …
    if (navigator.storage && navigator.storage.persist) {
        await navigator.storage.persist()
    }

    // … and forward realtime AppEvents to the notification channel (only while online).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AppEvent<any,any>'s
    // `type` is `keyof IAppEventType | null`, which doesn't fit the notification-received
    // payload's `type: string`; `any` keeps the old JS pass-through exactly (verbatim port).
    AppEvent.registerDefaultCallback((event: any) => {
        if (!isOnline.value) return

        appEvents.emit('notification-received', { type: event.type, data: event.data })
    })
})
</script>

<style>
/*
 * Canvas = the theme's paper surface (both themes; design §3 — the page canvas
 * is --ui-bg-muted), plus the Electron chrome behaviors (drag region + no text
 * selection — AdminLayout re-enables selection inside the admin shell).
 */
.app-container {
    background: var(--ui-bg-muted);
    -webkit-user-select: none;
    -webkit-app-region: drag;
}
</style>
